/**
=========================================================
* Material Kit 2 React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-kit-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
// Material Kit 2 React components
import Grid2 from "@mui/material/Unstable_Grid2";
import GeoJSON from "ol/format/GeoJSON";
import { Feature } from "ol";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Stroke, Style } from "ol/style";
import MKTypography from "../../../components/MKTypography";
import MKBox from "../../../components/MKBox";
import colors from "../../../assets/theme/base/colors";
import MKButton from "../../../components/MKButton";
import clearFlashingFeaturesLayer from "../../../utils/clearFlashingFeaturesLayer";
import convertToValidHttps from "../../../utils/convertToValidHttps";
import HTTP_API from "../../../store/constant/httpApi";

const { grey } = colors;

function AssetCard({ feature, map }) {
  const { id, geometry, properties } = feature;
  const link = HTTP_API.DATA_PATH.startsWith("s3://")
    ? convertToValidHttps(`${HTTP_API.DATA_PATH}/${feature.assets.data.href}`)
    : `${HTTP_API.DATA_PATH}/${feature.assets.data.href}`;

  const image = HTTP_API.DATA_PATH.startsWith("s3://")
    ? convertToValidHttps(`${HTTP_API.DATA_PATH}/${feature.assets.thumbnail.href}`)
    : `${HTTP_API.DATA_PATH}/${feature.assets.thumbnail.href}`;

  const viewAsset = () => {
    clearFlashingFeaturesLayer(map, "flashingFeaturesLayer");
    const geom4326 = new GeoJSON().readGeometry(geometry);
    const geom3857 = geom4326.transform("EPSG:4326", "EPSG:3857");
    const assetFeature = new Feature({
      geometry: geom3857,
    });

    const flashingStyle = [
      new Style({
        stroke: new Stroke({ color: "#e0061f", width: 2 }),
      }),
    ];
    const flashingFeaturesLayer = new VectorLayer({
      source: new VectorSource({ features: [assetFeature] }),
      style: flashingStyle,
      visible: true,
      zIndex: 2,
      className: `flashingFeaturesLayer-${id}`,
    });

    map.addLayer(flashingFeaturesLayer);
    map.getView().fit(geom3857.getExtent(), {
      padding: [50, 50, 50, 50],
    });
  };

  const propertiesList = ["year", "extent"];
  const handleDownload = () => {
    // 这个错误可能是由于浏览器安全机制限制导致的。浏览器默认在非安全环境下（例如 http://）禁止下载文件。当您的页面是通过非安全的 http 协议加载时，浏览器可能不允许下载 TIF 格式的文件。
    //
    // 解决方法之一是，将链接协议改为 https。如果您使用的是自签名的证书，则需要在浏览器中添加该证书的信任。另一种解决方法是将 TIF 格式转换为其他格式，例如 PNG 或 JPG，这些格式可以在非安全环境下下载。
    fetch(link)
      .then((response) => response.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const dlink = document.createElement("a");
        dlink.href = url;
        dlink.setAttribute("download", `${id}.tif`);
        document.body.appendChild(dlink);
        dlink.click();
      })
      .catch((error) => console.error(error));
  };
  return (
    <MKBox
      sx={{
        bgcolor: grey[100],
        mb: 1,
        p: { lg: 0.5, xxl: 1 },
        background: "white",
        borderBottom: 0.5,
        // borderRadius: borders.borderRadius.xl,
        borderColor: "secondary.main",
      }}
    >
      <Grid2 container my="auto">
        <Grid2 xs={3} sx={{ display: "flex", justifyContent: "center" }}>
          <MKBox width="100%">
            <MKBox
              component="img"
              src={image}
              alt={id}
              width="100%"
              sx={{
                border: 1,
                height: 1,
                borderColor: grey[400],
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                objectFit: "cover",
                overflow: "hidden",
                borderRadius: 0,
              }}
            />
          </MKBox>
        </Grid2>
        <Grid2 xs={9}>
          <Grid2 container>
            <MKBox
              display="flex"
              justifyContent="space-between"
              sx={{ flexDirection: "column" }}
              px={1}
              lineHeight={0.5}
            >
              <Grid2 xs={12}>
                <MKTypography
                  color="secondary"
                  variant="body2"
                  sx={{
                    fontWeight: "bold",
                    lineHeight: 1.2,
                  }}
                >
                  {properties.title}
                </MKTypography>
              </Grid2>
              <Grid2 xs={12}>
                <MKBox sx={{ display: "inline-block", width: 1, py: 0.5 }}>
                  {propertiesList.map((item) => (
                    <MKTypography
                      key={item}
                      color="text"
                      variant="body2"
                      textTransform="capitalize"
                      sx={{
                        display: "inline",
                        wordWrap: "break-word",
                        pr: 1,
                        lineHeight: 1.2,
                      }}
                    >
                      {item} :{" "}
                      {properties[item].toString().replace(/~/g, " - ").replace(/,/g, " , ")}{" "}
                    </MKTypography>
                  ))}
                </MKBox>
              </Grid2>
              <Grid2 xs={12}>
                <Grid2 container>
                  <Grid2 xs={6}>
                    <MKButton
                      variant="text"
                      color="secondary"
                      component="a"
                      size="small"
                      sx={{ mx: 1, p: 0 }}
                      onClick={viewAsset}
                    >
                      zoom in
                    </MKButton>
                  </Grid2>
                  <Grid2 xs={6}>
                    <MKButton
                      variant="text"
                      color="secondary"
                      component="a"
                      href={link}
                      onClick={handleDownload}
                      size="small"
                      sx={{ mx: 1, p: 0 }}
                    >
                      Download
                    </MKButton>
                  </Grid2>
                </Grid2>
              </Grid2>
            </MKBox>
          </Grid2>
        </Grid2>
      </Grid2>
    </MKBox>
  );
}

// Typechecking props for the HorizontalTeamCard
AssetCard.propTypes = {
  map: PropTypes.oneOfType([PropTypes.any]).isRequired,
  feature: PropTypes.oneOfType([PropTypes.object, PropTypes.any]).isRequired,
};

export default AssetCard;
